import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTransactions } from '../../store/transactions';
import { Filters } from '../../store/interfaces';
import {
  TransactionFilters,
  PaginationState,
  SearchState,
  UseTransactionSearchParamsReturn,
  DEFAULT_FILTERS,
  DEFAULT_PAGINATION,
} from './types';
import { serializeSearchState, deserializeSearchState } from './serializer';

// Re-export types and constants for external consumers
export type { TransactionFilters, PaginationState, SearchState, UseTransactionSearchParamsReturn };
export { DEFAULT_FILTERS, DEFAULT_PAGINATION };

/**
 * Compara cada campo de Filter_State contra su valor por defecto.
 * Retorna true si al menos un campo difiere del default.
 *
 * Validates: Requirements 4.5, 4.6
 */
export function hasActiveFiltersCheck(filters: TransactionFilters): boolean {
  const filterKeys = Object.keys(DEFAULT_FILTERS) as (keyof TransactionFilters)[];
  return filterKeys.some((key) => filters[key] !== DEFAULT_FILTERS[key]);
}

/**
 * Hook principal que sincroniza el estado de búsqueda de transacciones con la URL.
 *
 * - Lee el estado inicial desde `location.search` usando `deserializeSearchState`
 * - `setFilters`: actualiza filtros parcialmente, resetea page a 1, usa `history.replace`
 * - `setPage`: actualiza page, preserva filtros, usa `history.push`
 * - `setPerPage`: actualiza per_page, resetea page a 1, usa `history.replace`
 * - `clearFilters`: resetea Filter_State a defaults, resetea page a 1, preserva per_page, usa `history.replace`
 * - `hasActiveFilters`: true si algún filtro difiere del default
 * - Dispara `getTransactions` cuando searchState cambia
 * - Detecta cambios de URL via `useLocation()` para manejar popstate (botón atrás)
 *
 * Validates: Requirements 1.1, 1.2, 1.3, 1.4, 2.1, 2.2, 2.3, 3.1, 3.2, 3.3, 3.4, 4.1, 4.2, 4.3, 4.4, 5.1, 5.2, 5.3, 5.4
 */
export function useTransactionSearchParams(): UseTransactionSearchParamsReturn {
  const history = useHistory();
  const location = useLocation();
  const { getTransactions } = useTransactions();

  // Derive searchState from current URL — this is the source of truth
  const searchState: SearchState = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return deserializeSearchState(params);
  }, [location.search]);

  /**
   * Dispatch getTransactions whenever searchState changes.
   * Converts page from 1-indexed (URL) to 0-indexed (API).
   * Converts TransactionFilters to the Filters interface expected by the store.
   */
  useEffect(() => {
    const apiPage = searchState.pagination.page - 1;
    const apiPageSize = searchState.pagination.per_page;

    // Convert TransactionFilters to the Filters dict expected by the store
    const storeFilters: Filters = {};
    const filterKeys = Object.keys(DEFAULT_FILTERS) as (keyof TransactionFilters)[];
    for (const key of filterKeys) {
      if (searchState.filters[key]) {
        storeFilters[key] = searchState.filters[key];
      }
    }

    getTransactions(apiPage, storeFilters, apiPageSize);
  }, [location.search]);

  /**
   * setFilters: update filters partially, reset page to 1, use history.replace.
   * Filter changes don't create new history entries.
   */
  const setFilters = useCallback(
    (newFilters: Partial<TransactionFilters>) => {
      const updatedState: SearchState = {
        filters: { ...searchState.filters, ...newFilters },
        pagination: { ...searchState.pagination, page: 1 },
      };

      const params = serializeSearchState(updatedState);
      const search = params.toString();
      history.replace({
        pathname: location.pathname,
        search: search ? `?${search}` : '',
      });
    },
    [searchState, history, location.pathname]
  );

  /**
   * setPage: update page, preserve filters, use history.push.
   * Page changes create history entries (back button navigates between pages).
   */
  const setPage = useCallback(
    (page: number) => {
      const updatedState: SearchState = {
        filters: { ...searchState.filters },
        pagination: { ...searchState.pagination, page },
      };

      const params = serializeSearchState(updatedState);
      const search = params.toString();
      history.push({
        pathname: location.pathname,
        search: search ? `?${search}` : '',
      });
    },
    [searchState, history, location.pathname]
  );

  /**
   * setPerPage: update per_page, reset page to 1, use history.replace.
   */
  const setPerPage = useCallback(
    (perPage: number) => {
      const updatedState: SearchState = {
        filters: { ...searchState.filters },
        pagination: { page: 1, per_page: perPage },
      };

      const params = serializeSearchState(updatedState);
      const search = params.toString();
      history.replace({
        pathname: location.pathname,
        search: search ? `?${search}` : '',
      });
    },
    [searchState, history, location.pathname]
  );

  /**
   * clearFilters: reset Filter_State to defaults, reset page to 1, preserve per_page.
   * Removes filter params from URL using history.replace.
   */
  const clearFilters = useCallback(() => {
    const updatedState: SearchState = {
      filters: { ...DEFAULT_FILTERS },
      pagination: { page: 1, per_page: searchState.pagination.per_page },
    };

    const params = serializeSearchState(updatedState);
    const search = params.toString();
    history.replace({
      pathname: location.pathname,
      search: search ? `?${search}` : '',
    });
  }, [searchState.pagination.per_page, history, location.pathname]);

  // Compute hasActiveFilters from current filters
  const hasActiveFilters = useMemo(
    () => hasActiveFiltersCheck(searchState.filters),
    [searchState.filters]
  );

  return {
    searchState,
    setFilters,
    setPage,
    setPerPage,
    clearFilters,
    hasActiveFilters,
  };
}

export default useTransactionSearchParams;
